import type { Action, Thunk, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { StoreModel } from '..';
import type { Injections } from '../services/injections';
import type { ApplicationErrorOptions } from '../../types';
import type {
  RateCalculationTestRecord,
  RateCalculationTestRequest,
  RateCalculationTestResponse,
  ServiceMethodParamCalendar,
  CarrierTestFileOptions,
  CarrierRateSelectionResponseMap,
  UsedCarrierMap,
  UpsReadyOptions,
  UpsReadyAuthResponse,
  FetchCarrierZonesParams,
  CarrierSummary,
  CarrierZoneSummary,
} from '../services/CarrierService/types';
import { readFileAsString, convertCsvToMatrix } from '../files/fileUtils';
import { differenceInMonths as monthDifference } from 'date-fns/differenceInMonths';
import parseStatusCode from '../errors/parseStatusCode';
import { endOfMonth } from 'date-fns/endOfMonth';
import type { ExpiringRate } from '../services/CarrierService/types';
import type { ServiceMethodConfigurationStatus } from './calendarCalculations';
import { getDatesCovered, getMonthsCovered, lastRateConfiguredDate } from './calendarCalculations';
import { buildCarrierRateRequests } from './carrierRateRequestBuilder';
import {
  adaptMegaMethodResponseToCarrierSelectionResponse,
  adaptRateCalculationTestRequestToMegaMethodPayload,
} from './megaMethodAdapter';
import { getLowestCostCarrierRateSelection } from '../services/CarrierService/carrierServiceUtils';
import type {
  CarrierCarrierInfo,
  CarrierCarrierServiceMethod,
  CarrierServiceMethodCreate,
  CarrierServiceMethodInfo,
} from '@shipium-corp/shipium-ts-types';

export interface SetActiveRateTableRowPayload {
  data: Record<string, number>;
  row: number;
  focusIndex?: number;
  selectedCarrierServiceMethod?: CarrierServiceMethodInfo;
}

export interface ActiveRateTableConfig {
  focusIndex?: number;
}
export type ActiveTabKey = 'service' | 'rate_table' | 'surcharges';

export const getSubUnitValue = (weightUnit: string, val: number) => {
  if (weightUnit === 'lb') {
    return val / 16;
  }
  if (weightUnit === 'kg') {
    return val / 1000;
  }
  return val / 16;
};

export const getParentUnitValue = (weightUnit: string, val: number) => {
  if (weightUnit === 'lb') {
    return val * 16;
  }
  if (weightUnit === 'kg') {
    return val * 1000;
  }
  return val;
};
export interface CarrierModel {
  activeRateCalculationTestRecord?: RateCalculationTestRecord;
  activeMegaMethodTestPayload?: any;
  rateCalculationTestRecords?: RateCalculationTestRecord[];
  rateCalculationTestRequestShipmentComplete: Computed<CarrierModel, boolean>;
  rateCalculationTestRequestShipFromComplete: Computed<CarrierModel, boolean>;
  rateCalculationTestRequestShipToComplete: Computed<CarrierModel, boolean>;
  rateCalculationTestRequestComplete: Computed<CarrierModel, boolean>;
  rawCarrierList: CarrierCarrierInfo[];
  carrierFailure: Action<CarrierModel, ApplicationErrorOptions>;
  setRateCalculationTestRequest: Action<CarrierModel, RateCalculationTestRequest>;
  setRateCalculationTestResponse: Action<CarrierModel, RateCalculationTestResponse | undefined>;
  setRateCalculationTestResponses: Action<CarrierModel, CarrierRateSelectionResponseMap | undefined>;
  setRateCalculationTestRecord: Action<CarrierModel, RateCalculationTestRecord>;
  setRateCalculationTestRecords: Action<CarrierModel, RateCalculationTestRecord[]>;
  fetchCarrierList: Thunk<CarrierModel, void, Injections, StoreModel, Promise<CarrierCarrierInfo[]>>;

  fetchCarrierRate: Thunk<
    CarrierModel,
    RateCalculationTestRequest,
    Injections,
    StoreModel,
    Promise<RateCalculationTestResponse>
  >;
  fetchBulkCarrierRates: Thunk<
    CarrierModel,
    RateCalculationTestRecord[],
    Injections,
    StoreModel,
    Promise<RateCalculationTestRecord[]>
  >;
  setCarrierList: Action<CarrierModel, any>;
  // Carrier Account

  carrierActionStart: Action<CarrierModel, void>;
  lastAction?: number;
  parseFileToBulkCarrierRateRecords: Thunk<
    CarrierModel,
    CarrierTestFileOptions,
    Injections,
    StoreModel,
    Promise<RateCalculationTestRecord[]>
  >;
  updateFulfillmentContextForBulkCarrierRateRecords: Thunk<
    CarrierModel,
    { fulfillmentContextId?: string },
    Injections,
    StoreModel,
    Promise<RateCalculationTestRecord[]>
  >;
  getDatesCovered: Computed<
    CarrierModel,
    (val: ServiceMethodParamCalendar[]) => Record<string, ServiceMethodParamCalendar>
  >;
  getMonthsCovered: Computed<
    CarrierModel,
    (val: ServiceMethodParamCalendar[]) => Record<string, [ServiceMethodParamCalendar]>
  >;
  getServiceMethodRateConfigurationStatus: Computed<CarrierModel, (val: Date) => ServiceMethodConfigurationStatus>;
  getLastConfiguredServiceMethodRateMonth: Computed<CarrierModel, (val: ServiceMethodParamCalendar[]) => Date>;
  expiringRates?: ExpiringRate[];
  setExpiringRates: Action<CarrierModel, ExpiringRate[]>;
  fetchExpiringRates: Thunk<CarrierModel, number, Injections, StoreModel, Promise<ExpiringRate[]>>;

  fetchUsedV2Carriers: Thunk<CarrierModel, void, Injections, StoreModel, Promise<UsedCarrierMap>>;
  usedV2Carriers: UsedCarrierMap;
  setUsedV2Carriers: Action<CarrierModel, UsedCarrierMap>;

  upsVerifyUri: string;
  setUpsVerifyUri: Action<CarrierModel, string>;
  fetchUpsVerifyUri: Thunk<CarrierModel, void, Injections, StoreModel, Promise<UpsReadyOptions>>;
  registerUpsAuthCodeLoading: boolean;
  setRegisterUpsAuthCodeLoading: Action<CarrierModel, boolean>;
  registerUpsAuthCodeError: string;
  setRegisterUpsAuthCodeError: Action<CarrierModel, string>;
  registerUpsAuthCode: Thunk<CarrierModel, string, Injections, StoreModel, Promise<void>>;
  upsVerification?: UpsReadyAuthResponse;
  setUpsVerification: Action<CarrierModel, UpsReadyAuthResponse | undefined>;
  retrieveUpsVerification: Thunk<CarrierModel, void, Injections, StoreModel, Promise<UpsReadyAuthResponse | undefined>>;
  // sell rate service methods
  fetchSellRateServiceMethods: Thunk<CarrierModel, void, Injections, StoreModel, Promise<CarrierServiceMethodInfo[]>>;
  sellRateServiceMethods: Record<string, CarrierServiceMethodInfo>;
  setSellRateServiceMethods: Action<CarrierModel, CarrierServiceMethodInfo[]>;
  createPartnerServiceMethod: Thunk<
    CarrierModel,
    CarrierServiceMethodCreate,
    Injections,
    StoreModel,
    Promise<CarrierServiceMethodInfo>
  >;
  // fetch single method
  fetchServiceMethod: Thunk<CarrierModel, string, Injections, StoreModel, Promise<CarrierCarrierServiceMethod>>;
  carrierServiceMethods: Record<string, CarrierCarrierServiceMethod>;
  setServiceMethod: Action<CarrierModel, CarrierCarrierServiceMethod>;

  carrierZones: CarrierZoneSummary[];
  setCarrierZones: Action<CarrierModel, CarrierZoneSummary[]>;
  fetchCarrierZones: Thunk<
    CarrierModel,
    FetchCarrierZonesParams,
    Injections,
    StoreModel,
    Promise<CarrierZoneSummary[]>
  >;

  fetchPartnerCarrierSummaries: Thunk<CarrierModel, void, Injections, StoreModel, Promise<CarrierSummary[]>>;
  partnerCarrierSummaries: CarrierSummary[];
  setCarrierSummaries: Action<CarrierModel, CarrierSummary[]>;
}

export const carrierModel: CarrierModel = {
  usedV2Carriers: {},
  activeMegaMethodTestPayload: {},
  rawCarrierList: [],
  carrierActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  getLastConfiguredServiceMethodRateMonth: computed((state) => (paramCalendar: ServiceMethodParamCalendar[]) => {
    const mCovered = state.getMonthsCovered(paramCalendar);
    return lastRateConfiguredDate(mCovered);
  }),
  getServiceMethodRateConfigurationStatus: computed((state) => (lastConfiguredServiceMethodRateMonth: Date) => {
    const monthDiff = monthDifference(endOfMonth(lastConfiguredServiceMethodRateMonth), new Date());
    if (monthDiff === 0) {
      return 'empty';
    }
    if (monthDiff <= 3) {
      return 'danger';
    }
    if (monthDiff < 12) {
      return 'incomplete';
    }
    return 'complete';
  }),
  getDatesCovered: computed((state) => {
    return (paramCalendar?: ServiceMethodParamCalendar[]) => {
      if (paramCalendar) {
        return getDatesCovered(paramCalendar);
      }
      return {};
    };
  }),
  getMonthsCovered: computed((state) => {
    return (paramCalendar?: ServiceMethodParamCalendar[]) => {
      if (paramCalendar) {
        return getMonthsCovered(paramCalendar);
      }
      return {};
    };
  }),
  setRateCalculationTestRequest: action((state, payload) => {
    state.activeRateCalculationTestRecord = state.activeRateCalculationTestRecord || { rowIndex: 0 };
    const customLabelEntries = { ...payload.customLabelEntries };
    state.activeRateCalculationTestRecord.request = { ...state.activeRateCalculationTestRecord.request, ...payload };
    state.activeRateCalculationTestRecord.request.customLabelEntries = customLabelEntries;
    state.activeMegaMethodTestPayload = adaptRateCalculationTestRequestToMegaMethodPayload({
      ...state.activeRateCalculationTestRecord.request,
      ...payload,
    });
  }),
  setRateCalculationTestResponse: action((state, payload) => {
    state.activeRateCalculationTestRecord = state.activeRateCalculationTestRecord || { rowIndex: 0 };
    state.activeRateCalculationTestRecord.response = payload;
  }),
  setRateCalculationTestResponses: action((state, payload) => {
    state.activeRateCalculationTestRecord = state.activeRateCalculationTestRecord || { rowIndex: 0 };
    state.activeRateCalculationTestRecord.responses = payload;
    if (payload && Object.keys(payload || {}).length > 0) {
      const lowest = getLowestCostCarrierRateSelection(payload);
      state.activeRateCalculationTestRecord = {
        ...state.activeRateCalculationTestRecord,
        rowIndex: 0,
        response: lowest.carrierSelection,
      };
    }
  }),
  setRateCalculationTestRecord: action((state, payload) => {
    state.activeRateCalculationTestRecord = payload;
  }),
  setRateCalculationTestRecords: action((state, payload) => {
    state.rateCalculationTestRecords = [...payload];
  }),
  setCarrierList: action((state, payload) => {
    state.rawCarrierList = payload;
  }),
  carrierFailure: action((state, payload) => {
    console.log(payload);
  }),
  fetchCarrierRate: thunk(async (actions, payload, { injections }) => {
    // hacky way to notify of any caught errors
    const checkErrorOccured = (res: CarrierRateSelectionResponseMap) => {
      return Object.values(res).find((val: any) => val.status === 500)?.error;
    };
    try {
      actions.carrierActionStart();
      if (!(payload.generateLabel === false)) {
        const response = adaptMegaMethodResponseToCarrierSelectionResponse(
          await injections.carrierService.megaMethodTest(payload),
        );
        actions.setRateCalculationTestResponse(response);
        return response;
      } else {
        const response = await injections.carrierService.carrierRateSelectionTest(payload);
        actions.setRateCalculationTestResponses(response);
        const detectedErr = checkErrorOccured(response);
        if (detectedErr) {
          throw new Error(String(detectedErr.message));
        }
        return response;
      }
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: `An error occured fetching one or more rates:${error.message}.`,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchBulkCarrierRates: thunk(async (actions, payload, { injections }) => {
    try {
      actions.carrierActionStart();
      const response = await injections.carrierService.bulkCarrierRateSelectionTest(payload);
      actions.setRateCalculationTestRecords(response);
      return response;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to process bulk carrier rates.',
        asNotification: true,
      });
      return error;
    }
  }),

  fetchCarrierList: thunk(async (actions, payload, { injections }) => {
    try {
      actions.carrierActionStart();
      const response = await injections.UIOrchestrationService.fetchCarriers();
      const formattedResponse = response
        .map((val) => {
          return {
            ...val,
            serviceMethods: val.serviceMethods.sort((a, b) => a.name.localeCompare(b.name)),
          };
        })
        .sort((a, b) => b?.name?.localeCompare(a?.name ?? '') ?? 0);
      actions.setCarrierList(formattedResponse);
      return formattedResponse;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to fetch carrier list.',
        asNotification: false,
      });
      return error;
    }
  }),
  rateCalculationTestRequestShipmentComplete: computed((state) => {
    if (
      !state.activeRateCalculationTestRecord?.request?.packageWeight?.weight ||
      !state.activeRateCalculationTestRecord?.request?.packageWeight?.weightUnit
    ) {
      return false;
    }
    if (!state.activeRateCalculationTestRecord?.request?.packagingMaterial) {
      return false;
    }
    if (
      !state.activeRateCalculationTestRecord?.request?.packagelinearDimensions?.length ||
      !state.activeRateCalculationTestRecord?.request?.packagelinearDimensions?.width ||
      !state.activeRateCalculationTestRecord?.request?.packagelinearDimensions?.height ||
      !state.activeRateCalculationTestRecord?.request?.packagelinearDimensions?.linearUnit
    ) {
      return false;
    }
    return true;
  }),
  rateCalculationTestRequestShipToComplete: computed((state) => {
    if (
      !state.activeRateCalculationTestRecord?.request?.shipmentDestinationAddress?.addressType ||
      !state.activeRateCalculationTestRecord?.request?.shipmentDestinationAddress?.address1 ||
      !state.activeRateCalculationTestRecord?.request?.shipmentDestinationAddress?.city ||
      !state.activeRateCalculationTestRecord?.request?.shipmentDestinationAddress?.state ||
      !state.activeRateCalculationTestRecord?.request?.shipmentDestinationAddress?.postalCode ||
      !state.activeRateCalculationTestRecord?.request?.shipmentDestinationAddress?.country
    ) {
      return false;
    }
    return true;
  }),
  rateCalculationTestRequestShipFromComplete: computed((state) => {
    if (
      !state.activeRateCalculationTestRecord?.request?.shipmentSourceAddress?.addressType ||
      !state.activeRateCalculationTestRecord?.request?.shipmentSourceAddress?.address1 ||
      !state.activeRateCalculationTestRecord?.request?.shipmentSourceAddress?.city ||
      !state.activeRateCalculationTestRecord?.request?.shipmentSourceAddress?.state ||
      !state.activeRateCalculationTestRecord?.request?.shipmentSourceAddress?.postalCode ||
      !state.activeRateCalculationTestRecord?.request?.shipmentSourceAddress?.country
    ) {
      return false;
    }
    return true;
  }),
  rateCalculationTestRequestComplete: computed((state) => {
    if (!state.activeRateCalculationTestRecord?.request?.shipmentEffectiveDate) {
      return false;
    }
    return true;
  }),

  parseFileToBulkCarrierRateRecords: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const state = getStoreState();
      actions.carrierActionStart();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      const csvString = await readFileAsString(payload.fileData);
      const csvMatrix = convertCsvToMatrix(csvString);
      const requests = buildCarrierRateRequests(csvMatrix, partnerId, payload.shipmentEffectiveDate);
      actions.setRateCalculationTestRecords(requests);
      return requests;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  updateFulfillmentContextForBulkCarrierRateRecords: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const state = getStoreState();
      actions.carrierActionStart();
      const requests = state.carrier.rateCalculationTestRecords;

      if (Array.isArray(requests)) {
        for (const record of requests) {
          if (record.request) {
            record.request.fulfillmentContext = payload.fulfillmentContextId;
          }
        }
        actions.setRateCalculationTestRecords(requests);
      }
      return requests;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  setExpiringRates: action((state, payload) => {
    state.expiringRates = payload;
  }),
  fetchExpiringRates: thunk(async (actions, payload, { getStoreState, injections }) => {
    try {
      actions.carrierActionStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      const labels = await injections.carrierService.fetchExpiringRates(partnerId, payload);
      actions.setExpiringRates(labels);
      return labels;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  setUsedV2Carriers: action((state, payload) => {
    state.usedV2Carriers = payload;
  }),
  fetchUsedV2Carriers: thunk(async (actions, payload, { injections }) => {
    try {
      actions.carrierActionStart();
      const res = await injections.carrierService.fetchUsedV2Carriers();
      actions.setUsedV2Carriers(res);
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  upsVerifyUri: '',
  setUpsVerifyUri: action((state, payload) => {
    state.upsVerifyUri = payload;
  }),
  fetchUpsVerifyUri: thunk(async (actions, payload, { injections }) => {
    try {
      const res: UpsReadyOptions = await injections.carrierService.fetchUpsVerifyOptions();
      actions.setUpsVerifyUri(res?.oauthRedirectUrl || '');
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  registerUpsAuthCodeLoading: false,
  setRegisterUpsAuthCodeLoading: action((state, payload) => {
    state.registerUpsAuthCodeLoading = payload;
  }),
  registerUpsAuthCodeError: '',
  setRegisterUpsAuthCodeError: action((state, payload) => {
    state.registerUpsAuthCodeError = payload;
  }),
  registerUpsAuthCode: thunk(async (actions, payload, { injections }) => {
    try {
      actions.setRegisterUpsAuthCodeLoading(true);
      const res = await injections.carrierService.saveUpsVerifyAuthCode(payload);
      actions.setRegisterUpsAuthCodeLoading(false);
      return res;
    } catch (error) {
      actions.setRegisterUpsAuthCodeLoading(false);
      actions.setRegisterUpsAuthCodeError('Unable to successfully persist the auth code. Please try again.');
      // This can happen just due to the user not being authenticated.
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: false,
      });
      return error;
    }
  }),
  setUpsVerification: action((state, payload) => {
    state.upsVerification = payload;
  }),
  retrieveUpsVerification: thunk(async (actions, payload, { injections }) => {
    try {
      const res: UpsReadyAuthResponse[] = await injections.carrierService.fetchUpsVerifyAuthCode();
      if (Array.isArray(res) && res.length > 0) {
        res.sort((a, b) => (a.createdAtTimestamp < b.createdAtTimestamp ? -1 : 1));
        actions.setUpsVerification(res[0]);

        await injections.carrierService.deleteUpsVerifyAuthCodes();
        return res[0];
      } else {
        actions.setUpsVerification(undefined);
        return undefined;
      }
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: false,
      });
      return undefined;
    }
  }),
  //#region Sell Rates
  sellRateServiceMethods: {},
  setSellRateServiceMethods: action((state, payload) => {
    state.sellRateServiceMethods = {};
    for (const val of payload) {
      state.sellRateServiceMethods[val?.serviceMethodId ?? ''] = val;
    }
  }),
  fetchSellRateServiceMethods: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.carrierService.fetchServiceMethods({
        serviceMethodType: 'simulation_sell',
        dataSource: 'data',
      });
      actions.setSellRateServiceMethods(res);
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: false,
      });
      throw error;
    }
  }),
  //#endregion
  carrierServiceMethods: {},
  setServiceMethod: action((state, payload) => {
    state.carrierServiceMethods = {
      ...state.carrierServiceMethods,
      [payload.serviceMethodInfo?.serviceMethodId ?? '']: payload,
    };
  }),
  fetchServiceMethod: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.carrierService.fetchServiceMethod(payload);
      actions.setServiceMethod(res);
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: false,
      });
      throw error;
    }
  }),
  carrierZones: [],
  setCarrierZones: action((state, payload) => {
    state.carrierZones = payload;
  }),
  fetchCarrierZones: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.UIOrchestrationService.fetchZonesForCarrier(
        payload.carriers,
        payload.showInternational,
      );
      actions.setCarrierZones(res);
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: false,
      });
      throw error;
    }
  }),
  setCarrierSummaries: action((state, payload) => {
    state.partnerCarrierSummaries = payload;
  }),
  fetchPartnerCarrierSummaries: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const state = getStoreState();
      const partnerId = state.session.user?.sessionPartnerId;
      const res = await injections.UIOrchestrationService.fetchPartnerCarrierSummaries(partnerId ?? '');
      actions.setCarrierSummaries(res);
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: false,
      });
      throw error;
    }
  }),
  partnerCarrierSummaries: [],
  createPartnerServiceMethod: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.carrierService.createPartnerServiceMethod(payload);
      return res;
    } catch (error) {
      actions.carrierFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
};
